import { useEffect } from "react";

import { InnerPageChrome } from "@/components/layout/InnerPageChrome";
import { SITE_ROUTES } from "@/config/siteRoutes";
import type { CmsContentPageData } from "@/pages/shared/cmsContent";
import { useCmsContentPage } from "@/pages/shared/cmsContent";

import styles from "./PrivacyPolicyPage.module.scss";

const fallbackParagraphs = [
  "VenueX IKE acts as the data controller for the personal information submitted through this website, including contact and project inquiry forms, email correspondence, and related business communications.",
  "We collect personal data only when it is provided voluntarily, such as your name, email address, phone number, project brief, or other information you choose to share. We use this information to respond to your inquiry, assess fit, coordinate the next steps, and manage the delivery of our services.",
  "We do not use your personal data for marketing communication without your explicit consent. Where consent is required, you may withdraw it at any time. We may also use technical data such as cookies and basic site analytics to keep the website secure, reliable, and functional in line with lawful purposes.",
  "The information is processed only for the legitimate purpose of running our business, fulfilling a contract, complying with legal obligations, or pursuing a legitimate interest that does not override your rights. We keep personal data only for as long as necessary and then delete or securely archive it in accordance with applicable law.",
  "Data may be shared with trusted service providers and internal teams who support our operations, such as email delivery, hosting, and technical support. Where required, we apply appropriate safeguards for international transfers and limit access to the minimum necessary.",
  "You have rights to request access to your data, request correction, ask for deletion, object to certain processing, restrict processing, and exercise your rights to data portability where applicable. You may also complain to the relevant data protection authority in your jurisdiction.",
  "If you have a question about personal data, or if you wish to exercise any of the above rights, contact us at info@venuex.gr.",
  "This policy may be updated from time to time to reflect operational, legal, or service changes. The most recent version is the version published on this page.",
];

const fallback: CmsContentPageData = {
  id: 9,
  slug: "privacy-policy",
  title: "VenueX privacy and data handling",
  intro: "How VenueX collects, uses and protects information submitted through this website.",
  seoTitle: "Privacy Policy",
  searchDescription: "VenueX privacy policy and personal data handling information.",
  url: SITE_ROUTES.privacyPolicy,
  paragraphs: fallbackParagraphs.map((text) => ({ html: `<p>${text}</p>`, text })),
  bodyHtml: fallbackParagraphs.map((text) => `<p>${text}</p>`).join(""),
  sections: [],
};

export function PrivacyPolicyPage() {
  const page = useCmsContentPage("privacy-policy", fallback);
  const presentation = page.presentation;
  const primaryLink = presentation?.primary_link ?? {
    label: "Back to contact",
    href: SITE_ROUTES.contact,
  };
  const secondaryLink = presentation?.secondary_link ?? {
    label: "Home",
    href: SITE_ROUTES.home,
  };

  useEffect(() => {
    document.title = `${page.seoTitle || page.title} | VenueX`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", page.searchDescription || page.intro);
  }, [page.intro, page.searchDescription, page.seoTitle, page.title]);

  return (
    <InnerPageChrome currentPath={SITE_ROUTES.privacyPolicy}>
      <main className={styles.page}>
        <div className={styles.inner}>
          <p className={styles.eyebrow}>{presentation?.eyebrow ?? "Privacy Policy"}</p>
          <h1 className={styles.title}>{page.title}</h1>
          {page.intro && <p className={styles.intro}>{page.intro}</p>}

          <div
            className={styles.body}
            dangerouslySetInnerHTML={{ __html: page.bodyHtml }}
          />

          <div className={styles.links}>
            {primaryLink.label && <a href={primaryLink.href}>{primaryLink.label}</a>}
            {secondaryLink.label && <a href={secondaryLink.href}>{secondaryLink.label}</a>}
          </div>
        </div>
      </main>
    </InnerPageChrome>
  );
}
